'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { isAppHostname, parseShellUa, type ShellPlatform } from '@/lib/runtime/is-native-app';
import { getBridge } from '@/lib/native/bridge';

export type NativeAppState = {
  /** Served from the dedicated app origin (app.inceptionia.com). */
  isAppOrigin: boolean;
  /**
   * The native bridge is actually present — the reliable signal that we're
   * inside the shell (correctly false when the app host is opened in a
   * normal browser). Starts false and flips post-mount to avoid hydration
   * mismatches against the CDN-cached server HTML.
   */
  isNativeShell: boolean;
  platform: ShellPlatform | 'web';
  shellVersion: string | null;
};

const defaultState: NativeAppState = {
  isAppOrigin: false,
  isNativeShell: false,
  platform: 'web',
  shellVersion: null,
};

const NativeAppContext = createContext<NativeAppState>(defaultState);

export const useNativeApp = (): NativeAppState => useContext(NativeAppContext);

export function NativeAppProvider({
  children,
  initialIsAppOrigin = false,
}: {
  children: React.ReactNode;
  /**
   * Resolved SERVER-SIDE from the request Host in app/[lang]/layout.tsx.
   *
   * This must be seeded rather than discovered post-mount: consumers that
   * withhold content on the app origin — above all the Amazon CTA gate in
   * `external-link.tsx` — would otherwise render the withheld markup into
   * the SSR HTML and only drop it after hydration. For an Associates policy
   * gate, "present in the document, then removed" is not withheld.
   *
   * Safe for hydration because the server and client agree: both derive it
   * from the same host. Responses already vary by host (the middleware
   * emits a different X-Robots-Tag), so this adds no new cache dimension.
   */
  initialIsAppOrigin?: boolean;
}) {
  const [state, setState] = useState<NativeAppState>({
    ...defaultState,
    isAppOrigin: initialIsAppOrigin,
  });

  useEffect(() => {
    // getBridge() lazily assembles the adapter from the injected Capacitor
    // runtime, so this is also what makes isNativeShell true in the shell.
    const bridge = getBridge();
    const ua = parseShellUa(navigator.userAgent);
    setState({
      isAppOrigin: initialIsAppOrigin || isAppHostname(window.location.hostname),
      isNativeShell: !!bridge,
      platform: bridge?.platform ?? ua.platform ?? 'web',
      shellVersion: bridge?.version ?? ua.version,
    });
    // Splash handoff: the shell holds the native splash until the web layer
    // signals it has painted (4s native hard-timeout as backstop).
    bridge?.appReady();
  }, [initialIsAppOrigin]);

  const value = useMemo(() => state, [state]);

  return <NativeAppContext.Provider value={value}>{children}</NativeAppContext.Provider>;
}
